import React, { useContext, useState } from 'react';
import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Image } from '../elements/image';
import { customTheme } from '../theme';
import { Charges, Item } from '../types';
import { getItemPrice, getPriceWithCurrency } from '../utils/charges';
import MenuItemModal from './menu-item-modal';
import { GET_CHARGES, GET_CONFIG_CHARGES, GET_VENUE } from '../graphql/query';
import { useQuery } from '@apollo/client';
import { Loader } from '../elements/loader';
import { useTranslation } from 'react-i18next';
import { AppConfigContext } from './view-item/types';
import { AppContext } from '../context/app-context';
import { SET_OUT_OF_STOCK_NOTIFICATION } from '../graphql/mutation';
import { useMutationHook } from '../hooks/use-mutation';
import { UserContext } from '../context/user-context';
import { LOGIN_ROUTE } from '../constants/routes';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

interface Props {
  item: Item;
  menuID: string;
  storeID?: string;
}

export function ItemCard({ item, storeID, menuID }: Props) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [notified, setNotified] = useState<boolean>(false);
  const { t } = useTranslation(['patron_portal', 'common']);
  const { stadium } = useContext(AppContext);
  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { loading: fetchingVenue, data: venueData } = useQuery(GET_VENUE, {
    variables: {
      id: stadium?.value,
    },
    onCompleted(data) {},
  });

  const { mutate: setOutOfStockNotification, loading: notifyLoading } =
    useMutationHook({
      mutation: SET_OUT_OF_STOCK_NOTIFICATION,
      onCompleted: (data) => {
        if (data?.setOutOfStockNotification?.success) {
          setNotified(true);
        }
        enqueueSnackbar('You will noticed once item is back', {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      },
      onError: (error) => {
        console.error('Notification Error:', error);
      },
    });

  const Context: AppConfigContext = {
    regionID: '6624830cad8487b3a4b4d966',
    accountID: venueData?.getVenue?.accountID,
    eventID: stadium?.value,
    venueID: stadium?.value,
  };

  const {
    data: getChargesData = {
      getConfigCharges: {},
    },
    loading: getChargesLoading,
  } = useQuery(GET_CONFIG_CHARGES, {
    variables: {
      context: Context,
    },
  });

  const charges = getChargesData.getConfigCharges as Charges;

  const openModal = () => {
    if (!item?.outOfStock) {
      setIsModalOpen(true);
      document.body.style.touchAction = 'none';
    }
  };

  const closeBottomSheet = () => {
    setIsModalOpen(false);
    document.body.style.touchAction = 'auto';
  };

  if (getChargesLoading) {
    return <Loader visible={true} />;
  }

  if (!item || item === undefined) {
    return <></>;
  }

    const handleNotifyClick = (e: React.MouseEvent) => {
      if (!userContext.isLoggedIn) {
      navigate(LOGIN_ROUTE);
      return;
    }
    e.stopPropagation();
    if (notifyLoading || notified) return;

    setOutOfStockNotification({
      variables: {
        itemId: item.id,
        patronId:userContext?.patron?._id,
        patronMobileNumber: userContext?.phoneNumber,
      },
    });
  };

  return (
    <>
      {
        <MenuItemModal
          item={item}
          menuID={menuID}
          storeID={storeID}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          onClose={closeBottomSheet}
        />
      }
      <Box
        display={'flex'}
        width={'100%'}
        height={'100%'}
        justifyContent={'space-between'}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
        }}
        onClick={openModal}
      >
        <Box
          p={2}
          sx={{
            minWidth: {
              xs: '50px',
              sm: '350px',
            },
            maxWidth: {
              xs: '280px',
              sm: '380px',
            },
          }}
        >
          <Box>
            <Typography
              fontFamily={customTheme.fontFamily}
              fontSize={16}
              fontWeight={500}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item?.name}
            </Typography>
          </Box>
          {!item?.outOfStock ? (
            <Box mt={'2px'}>
              {
                <Typography
                  fontFamily={customTheme.fontFamily}
                  fontSize={14}
                  fontWeight={400}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {item.price === 0
                    ? t('common.label.selectOptions', { ns: 'common' })
                    : getPriceWithCurrency(getItemPrice(item, charges))}
                </Typography>
              }
            </Box>
          ) : (
            <Box mt={'2px'}>
              <Typography
                fontFamily={customTheme.fontFamily}
                fontSize={14}
                fontWeight={600}
                letterSpacing={1}
                color={'#FF50DD'}
              >
                {item?.outOfStockMessage || 'Out of stock'}
              </Typography>
              <Box
                mt={1}
                p={0.7}
                bgcolor={'#FF50DD'}
                borderRadius={2}
                display={'inline-block'}
                onClick={handleNotifyClick}
              >
                <Typography
                  fontFamily={customTheme.fontFamily}
                  fontSize={14}
                  fontWeight={500}
                  letterSpacing={1}
                  color={'white'}
                >
                  {notified
                    ? 'You will be notified'
                    : notifyLoading
                    ? 'Please wait...'
                    : 'Let Me Know When Available'}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        {item?.imageUrl && (
          <Box
            sx={{
              minWidth: '90px',
              height: '100%',
              width: {
                xs: '90px',
                sm: '150px',
                md: '180px',
                lg: '210px',
                xl: '210px',
              },
              filter: () =>
                item.outOfStock ? 'grayscale(100%)' : 'grayscale(0%)',
            }}
          >
            <Image
              style={{
                height: '100%',
                width: '100%',
                borderTopRightRadius: '12px',
                borderBottomRightRadius: '12px',
              }}
              src={item?.thumbImageUrl ? item?.thumbImageUrl : item?.imageUrl}
            />
          </Box>
        )}
      </Box>
    </>
  );
}
